import { useState } from "react";
import api, { apiError, maskDoc } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Building2, UserCog, MapPin, Phone, Landmark, KeyRound } from "lucide-react";
import { Field, SelectField, SettingsCard, SectionLabel, StatusPill } from "./ui";

const CORP_TYPES = [
  ["", "Selecione..."],
  ["MEI", "MEI"],
  ["EI", "Empresário Individual (EI)"],
  ["EIRELI", "EIRELI"],
  ["LTDA", "Sociedade Limitada (LTDA)"],
  ["SLU", "Soc. Limitada Unipessoal (SLU)"],
  ["SS", "Sociedade Simples"],
  ["SA", "Sociedade Anônima (S.A.)"],
  ["ASSOCIATION", "Associação"],
];
const ACCOUNT_TYPES = [["checking", "Corrente"], ["savings", "Poupança"]];
const PHONE_TYPES = [["mobile", "Celular"], ["home", "Fixo"]];
const digits = (v) => String(v || "").replace(/\D/g, "");

// Monta o estado inicial a partir do JSON salvo (pg_recipient_data) ou dos campos legados.
function initRec(form) {
  const d = form.pg_recipient_data || {};
  return {
    holder_name: d.holder_name ?? form.pg_holder_name ?? form.store_name ?? "",
    holder_document: d.holder_document ?? form.pg_holder_document ?? form.cnpj ?? "",
    email: d.email ?? "",
    bank: { bank: "", branch_number: "", branch_check_digit: "", account_number: "", account_check_digit: "", type: "checking", ...(d.bank || {}) },
    company: { company_name: "", trading_name: "", corporation_type: "", founding_date: "", annual_revenue: "", ...(d.company || {}) },
    partner: { name: "", document: "", email: "", birthdate: "", mother_name: "", monthly_income: "", professional_occupation: "", ...(d.partner || {}) },
    individual: { birthdate: "", mother_name: "", monthly_income: "", professional_occupation: "", ...(d.individual || {}) },
    address: { zip_code: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "", reference_point: "", ...(d.address || {}) },
    phone: { ddd: "", number: "", type: "mobile", ...(d.phone || {}) },
  };
}

export default function PaymentTab({ form, reload }) {
  const [rec, setRec] = useState(() => initRec(form));
  const [saving, setSaving] = useState(false);
  /* As chaves ficam FORA do `rec`: elas vão para `/admin/settings` (configuração
     da loja) e o resto vai para `/admin/settings/recipient` (cadastro no
     Pagar.me). São dois destinos e dois momentos — misturá-los faria salvar a
     chave depender de preencher o KYC inteiro. */
  const [chaves, setChaves] = useState({ pagarme_secret_key: "", pagarme_public_key: form.pagarme_public_key || "" });
  const [savingKeys, setSavingKeys] = useState(false);

  const isCnpj = digits(rec.holder_document).length > 11;
  const top = (k) => (v) => setRec((r) => ({ ...r, [k]: v }));
  const sub = (sec, k) => (v) => setRec((r) => ({ ...r, [sec]: { ...r[sec], [k]: v } }));

  const salvarChaves = async () => {
    setSavingKeys(true);
    try {
      await api.put("/admin/settings", chaves);
      toast.success("Chaves salvas");
      setChaves((c) => ({ ...c, pagarme_secret_key: "" }));   // some da tela de novo
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingKeys(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        holder_name: rec.holder_name,
        holder_document: rec.holder_document,
        email: rec.email,
        bank: rec.bank,
        address: rec.address,
        phone: rec.phone,
        ...(isCnpj ? { company: rec.company, partner: rec.partner } : { individual: rec.individual }),
      };
      const { data } = await api.post("/admin/settings/recipient", payload);
      toast.success(data.recipient_id ? "Recebedor criado no Pagar.me" : (data.note || "Dados do recebedor salvos"));
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <SettingsCard
      title="Pagamentos (Pagar.me)"
      icon={CreditCard}
      right={<StatusPill ok={form.pagarme_ready} okText="Recebendo" pendingText={form.pagarme_key_set ? "Falta criar o recebedor" : "Falta configurar a chave"} />}
    >
      {/* ── Chaves ──────────────────────────────────────────────────────────
          Vêm PRIMEIRO porque são o primeiro obstáculo: sem elas nada do resto
          desta tela produz cobrança. Antes só existiam no `.env`, e o painel
          avisava "falta configurar a chave" sem oferecer onde — o único caminho
          era editar arquivo no servidor por SSH. */}
      <SectionLabel><span className="inline-flex items-center gap-1.5"><KeyRound size={13} /> Chaves da API</span></SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Chave secreta (sk_...)"
          type="password"
          value={chaves.pagarme_secret_key}
          onChange={(v) => setChaves((c) => ({ ...c, pagarme_secret_key: v }))}
          placeholder={form.pagarme_key_set ? "•••••••• (já configurada)" : "sk_live_..."}
          testId="settings-pg-secret"
          hint={form.pagarme_key_from_env
            ? "Hoje vindo do arquivo .env do servidor. Preencher aqui passa a usar esta."
            : "É ela que cobra. Fica só no servidor, criptografada — deixe em branco para manter a atual."}
        />
        <Field
          label="Chave pública (pk_...)"
          value={chaves.pagarme_public_key}
          onChange={(v) => setChaves((c) => ({ ...c, pagarme_public_key: v }))}
          placeholder="pk_live_..."
          testId="settings-pg-public"
          hint="Vai no navegador do cliente para validar o cartão. Sem ela, o cartão não é oferecido na loja."
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button onClick={salvarChaves} disabled={savingKeys} data-testid="settings-pg-keys-btn"
          className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg">
          {savingKeys ? "Salvando..." : "Salvar chaves"}
        </button>
        <a href="https://dashboard.pagar.me/#/settings/keys" target="_blank" rel="noreferrer"
          className="text-sm text-brand-600 hover:underline">Onde encontrar minhas chaves →</a>
      </div>

      {/* O estado do CARTÃO em separado: é a forma de pagamento que some da loja
          quando falta configuração, e sem dizer isso aqui ninguém liga uma coisa
          à outra — some da vitrine sem explicação. */}
      <div className={`mt-4 rounded-lg border p-3 text-xs leading-relaxed ${
        form.card_enabled ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
        data-testid="settings-pg-card-status">
        {form.card_enabled
          ? "Cartão de crédito está sendo oferecido na loja, em até 12x."
          : "Cartão de crédito NÃO aparece na loja. Ele exige as duas chaves e o recebedor criado — PIX e boleto funcionam só com a chave secreta e o recebedor."}
      </div>

      {/* Titular */}
      <div className="border-t border-slate-100 pt-5 mt-6" />
      <SectionLabel>Titular do recebedor</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2"><Field label="Nome / Razão social" required value={rec.holder_name} onChange={top("holder_name")} testId="settings-pg-holder" /></div>
        <Field label="CPF ou CNPJ" required mono value={maskDoc(rec.holder_document)} onChange={(v) => top("holder_document")(v)} hint={isCnpj ? "CNPJ → recebedor Pessoa Jurídica" : "CPF → recebedor Pessoa Física"} />
        <div className="md:col-span-3"><Field label="E-mail do recebedor" required type="email" value={rec.email} onChange={top("email")} /></div>
      </div>

      {isCnpj ? (
        <>
          {/* Empresa */}
          <div className="border-t border-slate-100 pt-5 mt-6"><SectionLabel><span className="inline-flex items-center gap-1.5"><Building2 size={13} /> Empresa</span></SectionLabel></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Razão social" required value={rec.company.company_name} onChange={sub("company", "company_name")} />
            <Field label="Nome fantasia" required value={rec.company.trading_name} onChange={sub("company", "trading_name")} />
            <SelectField label="Tipo societário" required value={rec.company.corporation_type} onChange={sub("company", "corporation_type")} options={CORP_TYPES} />
            <Field label="Data de fundação" required type="date" value={rec.company.founding_date} onChange={sub("company", "founding_date")} />
            <Field label="Faturamento anual (R$)" required type="number" value={rec.company.annual_revenue} onChange={sub("company", "annual_revenue")} hint="Faturamento bruto anual estimado." />
          </div>

          {/* Sócio administrador */}
          <div className="border-t border-slate-100 pt-5 mt-6"><SectionLabel><span className="inline-flex items-center gap-1.5"><UserCog size={13} /> Sócio administrador</span></SectionLabel></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome completo" required value={rec.partner.name} onChange={sub("partner", "name")} />
            <Field label="CPF" required mono value={maskDoc(rec.partner.document)} onChange={sub("partner", "document")} />
            <Field label="E-mail" required type="email" value={rec.partner.email} onChange={sub("partner", "email")} />
            <Field label="Data de nascimento" required type="date" value={rec.partner.birthdate} onChange={sub("partner", "birthdate")} />
            <Field label="Nome da mãe" value={rec.partner.mother_name} onChange={sub("partner", "mother_name")} />
            <Field label="Renda mensal (R$)" required type="number" value={rec.partner.monthly_income} onChange={sub("partner", "monthly_income")} />
            <div className="md:col-span-2"><Field label="Profissão / ocupação" required value={rec.partner.professional_occupation} onChange={sub("partner", "professional_occupation")} /></div>
          </div>
        </>
      ) : (
        <>
          {/* Pessoa física */}
          <div className="border-t border-slate-100 pt-5 mt-6"><SectionLabel><span className="inline-flex items-center gap-1.5"><UserCog size={13} /> Dados pessoais</span></SectionLabel></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Data de nascimento" required type="date" value={rec.individual.birthdate} onChange={sub("individual", "birthdate")} />
            <Field label="Nome da mãe" value={rec.individual.mother_name} onChange={sub("individual", "mother_name")} />
            <Field label="Renda mensal (R$)" required type="number" value={rec.individual.monthly_income} onChange={sub("individual", "monthly_income")} />
            <Field label="Profissão / ocupação" required value={rec.individual.professional_occupation} onChange={sub("individual", "professional_occupation")} />
          </div>
        </>
      )}

      {/* Endereço */}
      <div className="border-t border-slate-100 pt-5 mt-6"><SectionLabel><span className="inline-flex items-center gap-1.5"><MapPin size={13} /> Endereço</span></SectionLabel></div>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="md:col-span-2"><Field label="CEP" required mono value={rec.address.zip_code} onChange={sub("address", "zip_code")} /></div>
        <div className="md:col-span-3"><Field label="Rua / logradouro" required value={rec.address.street} onChange={sub("address", "street")} /></div>
        <Field label="Número" required value={rec.address.number} onChange={sub("address", "number")} />
        <div className="md:col-span-2"><Field label="Complemento" value={rec.address.complement} onChange={sub("address", "complement")}
          hint="Em branco vai como N/A — a Pagar.me exige o campo presente." /></div>
        <div className="md:col-span-2"><Field label="Bairro" required value={rec.address.neighborhood} onChange={sub("address", "neighborhood")} /></div>
        <div className="md:col-span-2"><Field label="Cidade" required value={rec.address.city} onChange={sub("address", "city")} /></div>
        <Field label="UF" required value={rec.address.state} onChange={(v) => sub("address", "state")(v.toUpperCase().slice(0, 2))} />
        <div className="md:col-span-5"><Field label="Ponto de referência" value={rec.address.reference_point} onChange={sub("address", "reference_point")}
          hint="Em branco vai como N/A — a Pagar.me exige o campo presente, mesmo sem conteúdo." /></div>
      </div>

      {/* Telefone */}
      <div className="border-t border-slate-100 pt-5 mt-6"><SectionLabel><span className="inline-flex items-center gap-1.5"><Phone size={13} /> Telefone</span></SectionLabel></div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Field label="DDD" required mono value={rec.phone.ddd} onChange={sub("phone", "ddd")} />
        <div className="md:col-span-2"><Field label="Número" required mono value={rec.phone.number} onChange={sub("phone", "number")} /></div>
        <SelectField label="Tipo" value={rec.phone.type} onChange={sub("phone", "type")} options={PHONE_TYPES} />
      </div>

      {/* Conta bancária */}
      <div className="border-t border-slate-100 pt-5 mt-6"><SectionLabel><span className="inline-flex items-center gap-1.5"><Landmark size={13} /> Conta bancária de repasse</span></SectionLabel></div>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="md:col-span-2"><Field label="Banco (código)" required value={rec.bank.bank} onChange={sub("bank", "bank")} placeholder="ex.: 341" hint="Código de compensação (3 dígitos)." /></div>
        <div className="md:col-span-2"><Field label="Agência" required value={rec.bank.branch_number} onChange={sub("bank", "branch_number")} /></div>
        <Field label="Dígito ag." value={rec.bank.branch_check_digit} onChange={sub("bank", "branch_check_digit")} />
        <div />
        <div className="md:col-span-2"><Field label="Conta (sem dígito)" required value={rec.bank.account_number} onChange={sub("bank", "account_number")} /></div>
        <Field label="Dígito conta" required value={rec.bank.account_check_digit} onChange={sub("bank", "account_check_digit")} />
        <div className="md:col-span-2"><SelectField label="Tipo de conta" value={rec.bank.type} onChange={sub("bank", "type")} options={ACCOUNT_TYPES} /></div>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button onClick={submit} disabled={saving} data-testid="settings-recipient-btn"
          className="bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg">
          {saving ? "Salvando..." : (form.pagarme_recipient_id ? "Atualizar recebedor" : "Criar recebedor")}
        </button>
        <p className="text-xs text-slate-400">Com o recebedor cadastrado, os pedidos online geram cobrança real (PIX, cartão ou boleto).</p>
      </div>
    </SettingsCard>
  );
}
